// @ts-strict-ignore
import { Page, pickSeoProps } from '@components/Page'
import { RolexBreadcrumbs } from '@components/_rolex/Breadcrumbs'
import { RolexContainer } from '@components/_rolex/Container'
import { RolexImage } from '@components/_rolex/Image'
import RolexPage from '@components/_rolex/Page'
import { RolexPageIntro } from '@components/_rolex/PageIntro'
import { RolexPushBanner } from '@components/_rolex/push-banner-grid/PushBanner'
import RetailerList from '@components/_rolex/retailer-list'
import RetailerMap from '@components/_rolex/retailer-map'
import KeepExploring from '@components/_rolex/sliders/KeepExploring'
import { CleanButton } from '@components/gassan-ui'
import { Heading } from '@components/rolex-ui/Heading'
import { SecondaryCta } from '@components/rolex-ui/SecondaryCta'
import { Text } from '@components/rolex-ui/Text'
import { cn } from '@lib/cn'
import { i18nTranslationNamespaces } from '@lib/i18n-translation-files'
import {
  MultipleItemResponse,
  SingleItemResponse,
  getAllRolexRetailers,
  getPageByCodename,
} from '@lib/kontent'
import { RolexContactPage2979142 } from '@lib/kontent/models/RolexContactPage2979142'
import { RolexStorePageV72024 } from '@lib/kontent/models/RolexStorePageV72024'
import { AnimatePresence, motion } from 'framer-motion'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import { FC, useState } from 'react'

type Props = SingleItemResponse<RolexContactPage2979142> & {
  retailers: MultipleItemResponse<RolexStorePageV72024>
}

const RolexContact: FC<Props> = ({ item, modular_content, retailers }) => {
  const { t } = useTranslation(['other', 'rolex'])
  const { locale } = useRouter()
  const [retailerView, setRetailerView] = useState<'map' | 'list'>('map')
  const keepExploringItems = item.elements.keep_exploring.value.map(
    (key) => modular_content[key].elements,
  )

  return (
    <Page {...pickSeoProps(item.elements)} slug={'rolex/contact'} noindex={locale === 'de'}>
      <RolexPage pageType="contact page">
        <RolexBreadcrumbs
          items={[]}
          currentPageTitle={t('navigation.contactUs', { ns: 'rolex' })}
        />
        <RolexImage
          size="xl"
          mobileImage={item.elements.intro_mobile_image}
          desktopImage={item.elements.intro_desktop_image}
          className="!mb-0"
        />
        <div className="bg-rolexLightBeige pb-[60px] md:pb-[90px]">
          <RolexPageIntro
            title={item.elements.title.value}
            description={item.elements.description}
            titleTag="h1"
            children={
              <div className="mt-4">
                <SecondaryCta
                  as="link"
                  href={`/rolex${t('rolex.sendAMessage', { ns: 'navigation' })}`}
                  iconRight="chevron-right"
                >
                  {t('contactUs', { ns: 'rolex' })}
                </SecondaryCta>
              </div>
            }
          />
          <RolexContainer className="mt-[10vh]">
            <div className="mb-5 flex items-center gap-8">
              <CleanButton
                onClick={() => setRetailerView('map')}
                className={cn(
                  'mr-2',
                  retailerView === 'map'
                    ? 'pointer-events-none text-rolexPrimary'
                    : 'pointer-events-auto',
                )}
              >
                <Text variant="fixed16">
                  <>{t('viewMap', { ns: 'other' })}</>
                  <span className={retailerView === 'map' ? 'opacity-100' : 'opacity-0'}> ●</span>
                </Text>
              </CleanButton>
              <CleanButton
                onClick={() => setRetailerView('list')}
                className={cn(
                  'mr-2',
                  retailerView === 'list'
                    ? 'pointer-events-none text-rolexPrimary'
                    : 'pointer-events-auto',
                )}
              >
                <Text variant="fixed16">
                  <>{t('viewList', { ns: 'other' })}</>
                  <span className={retailerView === 'list' ? 'opacity-100' : 'opacity-0'}> ●</span>
                </Text>
              </CleanButton>
            </div>
            <AnimatePresence exitBeforeEnter>
              <motion.div
                key={retailerView}
                initial={{ y: -5, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -5, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {retailerView === 'list' ? (
                  <RetailerList {...retailers} />
                ) : (
                  <RetailerMap {...retailers} />
                )}
              </motion.div>
            </AnimatePresence>
          </RolexContainer>
        </div>
        <RolexImage
          mobileImage={item.elements.featured_mobile_image}
          desktopImage={item.elements.featured_desktop_image}
          size="xl"
          className="!mb-0"
        />
        <div className="bg-rolexLightBeige py-[60px] md:py-[90px]">
          <RolexContainer>
            <Heading variant="headline30" className="mb-4">
              {t('contactUs')}
            </Heading>
            <RolexPushBanner
              title={t('cta.message', { ns: 'rolex' })}
              buttonLink={`/rolex/${t('rolex.sendAMessage', { ns: 'navigation' })}`}
              buttonText={t('contactUs')}
              mobileImage={item.elements.contact_us_mobile_image}
              desktopImage={item.elements.contact_us_desktop_image}
              gridLayout="n1_column"
            />
          </RolexContainer>
        </div>
        <KeepExploring className="py-[30px] md:py-[60px]" items={keepExploringItems} />
      </RolexPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, preview, params }) => {
  try {
    const language = ['sg', 'de'].includes(locale) ? 'en' : locale
    const content = await getPageByCodename<RolexContactPage2979142>({
      codename: 'rolex_contact_page',
      lang: language,
      preview,
    })
    const retailers = await getAllRolexRetailers({ language, preview })
    return {
      props: {
        ...content,
        retailers: retailers as MultipleItemResponse<RolexStorePageV72024>,
        ...(await serverSideTranslations(language, i18nTranslationNamespaces)),
      },
    }
  } catch (error) {
    throw error
  }
}

export default RolexContact
